import { defineStore } from "pinia";
import api from "@/modules/api";
import { readMatePopularTags, readTextImagePopularTags } from "@/apis/explore";
import { readMatePopularPageFroChat, readPopularChatScene } from "@/apis/chat";
import { isMobile } from "@/modules/common";
import { useMate } from "@/stores/mate";
import { ChatSceneWithChatInfo, MateWithChatInfo } from "@/types/chat";
import { useReply } from "@/stores/reply";
import { useChatScene } from "@/stores/chatScene";

interface State {
  init: boolean;
  list: MatePopular[];
  loadingMateTags: boolean;
  tagsList: string[];
  mateTagList: { tag: string; count: number }[];
  explore: {
    featuredList: string[];
    sceneList: number[];
  };
}

export const usePopular = defineStore("popular", {
  state: (): State => {
    return {
      init: false,
      list: [],
      tagsList: [],
      loadingMateTags: false,
      mateTagList: [],
      explore: {
        featuredList: [],
        sceneList: [],
      },
    };
  },
  getters: {
    mateMap(state) {
      const map = new Map<string, MatePopular>();
      state.list?.forEach((mate) => {
        map.set(mate.uuid, mate);
      });
      return map;
    },
    featuredMateList(state) {
      const mateStore = useMate();
      return state.explore.featuredList.map(
        (uuid) => mateStore.mateMap.get(uuid)!
      ) as unknown as MateWithChatInfo[];
    },
    featuredChatSceneList(state) {
      const chatSceneStore = useChatScene();
      return state.explore.sceneList.map(
        (id) => chatSceneStore.getEntity(id) as ChatSceneWithChatInfo
      );
    },
  },
  actions: {
    /**
     * 获取Mate列表
     * 1. 如果已经请求过，直接返回
     * 2. 如果没有请求过，请求并返回
     */
    async getMatePopularList(): Promise<MatePopular[]> {
      if (this.list.length > 0 && this.init) {
        return this.list;
      } else {
        return await this.fetchMatePopularList();
      }
    },
    /**
     * 从服务端获取Mate列表
     */
    async fetchMatePopularList(): Promise<MatePopular[]> {
      const res = await api.readMatePopular();
      const mateList = res.itemzz;
      this.list = mateList;
      this.init = true;

      return mateList;
    },

    getOrFetchPopularTextImageTags() {
      if (this.tagsList.length > 0) {
        return this.tagsList;
      } else {
        return this.fetchPopularTextImageTags();
      }
    },
    getOrFetchMateTags() {
      if (this.mateTagList.length > 0) {
        return this.mateTagList;
      } else {
        return this.fetchPopularMateTags();
      }
    },
    async fetchPopularTextImageTags() {
      const { data } = await readTextImagePopularTags();
      this.tagsList = data;
      return this.tagsList;
    },
    async fetchPopularMateTags() {
      this.loadingMateTags = true;
      try {
        const { data } = await readMatePopularTags();
        this.mateTagList = data;
      } finally {
        this.loadingMateTags = false;
      }
      return this.mateTagList;
    },
    async getOrFetchFeaturedMateList(force = false) {
      if (this.explore.featuredList.length > 0 && !force) {
        return this.featuredMateList;
      }
      const res = await readMatePopularPageFroChat({
        filter: { isRecommended: 1 },
        pagination: { perPage: isMobile() ? 24 : 32, page: 1 },
      });
      const mateList = res.page.data;
      const mateStore = useMate();
      const replyStore = useReply();
      const chatSceneStore = useChatScene();
      mateStore.updateMateMap(mateList, false);
      mateList.forEach((m) => {
        const sceneList = m.chatScenezz ?? [];
        sceneList.forEach((scene) => {
          chatSceneStore.updateMap([scene]);
          if (scene.reply) {
            scene.reply = replyStore.updateMap([scene.reply])[0];
          }
        });
      });
      this.explore.featuredList = mateList.map((mate) => mate.uuid);
    },
    async getOrFetchChatScenePopularList(force = false) {
      if (this.explore.sceneList.length > 0 && !force) {
        return this.featuredChatSceneList;
      }
      const res = await readPopularChatScene({
        filter: { isRecommended: 1 },
        pagination: {
          page: 1,
          perPage: isMobile() ? 24 : 32,
        },
      });
      const sceneList = res.page.data;
      const chatSceneStore = useChatScene();
      const replyStore = useReply();
      sceneList.forEach((scene) => {
        chatSceneStore.updateMap([scene]);
        if (scene.reply) {
          scene.reply = replyStore.updateMap([scene.reply])[0];
        }
      });
      this.explore.sceneList = sceneList.map((scene) => scene.id!);
    },
  },
});
